/**
 * TemplateEditorPage — `/printing/templates/:id` (or `/new`).
 *
 * Loads a template via `GET /v1/printing/templates/:id`. Allows editing the
 * htmlBody, saving via `PUT /v1/printing/templates/:id`, and rendering a
 * live preview via `POST /v1/printing/templates/:id/preview`.
 *
 * Creating a new template uses a form on the /new route that calls
 * `POST /v1/printing/templates`.
 */
import { type JSX, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../lib/api/client';
import {
  createPrintTemplate,
  getPrintTemplate,
  previewPrintTemplate,
  updatePrintTemplate,
} from '../../lib/api/printing';
import { DeferredNotice, PageHeader, StatusBadge } from '../shared/StaffUI';

export function TemplateEditorPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const qc = useQueryClient();

  // ── Existing-template fetch ────────────────────────────────────────────────
  const template = useQuery({
    queryKey: ['printing', 'template', id],
    queryFn: () => getPrintTemplate(id ?? ''),
    enabled: !isNew && !!id,
    retry: false,
  });

  const [body, setBody] = useState('');
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);

  useEffect(() => {
    if (template.data) setBody(template.data.htmlBody);
  }, [template.data]);

  // ── New-template form state ────────────────────────────────────────────────
  const [newKey, setNewKey] = useState('');
  const [newKind, setNewKind] = useState<'THERMAL_4x6' | 'THERMAL_88MM' | 'LASER_A4' | 'INKJET_A4'>('THERMAL_4x6');
  const [newBody, setNewBody] = useState('<div>{{trackingNumber}}</div>');
  const [newDesc, setNewDesc] = useState('');

  const createMut = useMutation({
    mutationFn: () =>
      createPrintTemplate({
        templateKey: newKey,
        kind: newKind,
        htmlBody: newBody,
        variablesSchema: { type: 'object' as const, properties: {} },
        description: newDesc || undefined,
      }),
    onSuccess: (created) => {
      void qc.invalidateQueries({ queryKey: ['printing', 'templates'] });
      void navigate(`/printing/templates/${encodeURIComponent(created.id)}`);
    },
  });

  // ── Save (update) ─────────────────────────────────────────────────────────
  const saveMut = useMutation({
    mutationFn: () => updatePrintTemplate(id ?? '', { htmlBody: body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['printing', 'template', id] });
      void qc.invalidateQueries({ queryKey: ['printing', 'templates'] });
    },
  });

  // ── Preview ───────────────────────────────────────────────────────────────
  const previewMut = useMutation({
    mutationFn: () =>
      previewPrintTemplate(id ?? '', { trackingNumber: 'PRJ-2026-NG-DEMO', amount: '₦500.00' }),
    onSuccess: (result) => {
      setPreviewHtml(result.rendered);
    },
  });

  const denied =
    template.isError &&
    template.error instanceof ApiError &&
    (template.error.status === 401 || template.error.status === 403);

  if (isNew) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="New template"
          description="Author a new print template."
          actions={
            <Link to="/printing/templates" className="text-xs font-semibold text-nipost-green hover:underline">
              ← Back to templates
            </Link>
          }
        />
        <div className="bg-white border border-border rounded-xl p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-muted mb-1">Template key</label>
              <input
                data-testid="template-key"
                type="text"
                value={newKey}
                onChange={(e) => setNewKey(e.target.value)}
                placeholder="label.standard.v1"
                className="w-full px-3 py-2 border border-border rounded-lg text-sm font-mono"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted mb-1">Kind</label>
              <select
                data-testid="template-kind"
                value={newKind}
                onChange={(e) => setNewKind(e.target.value as typeof newKind)}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm"
              >
                <option value="THERMAL_4x6">THERMAL_4x6</option>
                <option value="THERMAL_88MM">THERMAL_88MM</option>
                <option value="LASER_A4">LASER_A4</option>
                <option value="INKJET_A4">INKJET_A4</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted mb-1">Description</label>
            <input
              type="text"
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
              placeholder="Optional description"
              className="w-full px-3 py-2 border border-border rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted mb-1">HTML body</label>
            <textarea
              data-testid="template-body"
              value={newBody}
              onChange={(e) => setNewBody(e.target.value)}
              rows={10}
              className="w-full px-3 py-2 border border-border rounded-lg text-xs font-mono"
            />
          </div>
          {createMut.isError ? (
            <p className="text-xs text-red-600">{(createMut.error as Error).message}</p>
          ) : null}
          <button
            type="button"
            data-testid="create-template-btn"
            disabled={createMut.isPending || !newKey || !newBody}
            onClick={() => createMut.mutate()}
            className="px-4 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark disabled:opacity-50"
          >
            {createMut.isPending ? 'Creating…' : 'Create template'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Edit template"
        description={`Inspect and update template ${id}.`}
        actions={
          <Link to="/printing/templates" className="text-xs font-semibold text-nipost-green hover:underline">
            ← Back to templates
          </Link>
        }
      />

      {template.isPending ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : denied ? (
        <DeferredNotice title="Permission required">
          The dev token doesn&apos;t carry <code className="font-mono">Printing/read</code>.
        </DeferredNotice>
      ) : template.isError ? (
        <DeferredNotice title="Template unavailable">{(template.error as Error).message}</DeferredNotice>
      ) : template.data ? (
        <div className="space-y-4">
          {/* ── Metadata ── */}
          <div className="bg-white border border-border rounded-xl p-5 space-y-2">
            <div className="flex items-center gap-3">
              <p className="text-sm font-semibold text-gray-900 font-mono">{template.data.templateKey}</p>
              <StatusBadge status={template.data.active ? 'ACTIVE' : 'PENDING'} />
              <span className="text-xs text-muted">v{template.data.version}</span>
              <span className="text-xs text-muted">{template.data.kind}</span>
            </div>
            {template.data.description ? (
              <p className="text-xs text-muted">{template.data.description}</p>
            ) : null}
          </div>

          {/* ── Editor ── */}
          <div className="bg-white border border-border rounded-xl p-5">
            <label className="block text-xs font-medium text-muted mb-2">HTML body</label>
            <textarea
              data-testid="template-html-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={16}
              className="w-full px-3 py-2 border border-border rounded-lg text-xs font-mono"
            />
            <div className="flex items-center gap-3 mt-3">
              <button
                type="button"
                data-testid="save-template-btn"
                disabled={saveMut.isPending}
                onClick={() => saveMut.mutate()}
                className="px-4 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark disabled:opacity-50"
              >
                {saveMut.isPending ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                data-testid="preview-template-btn"
                disabled={previewMut.isPending}
                onClick={() => previewMut.mutate()}
                className="px-4 py-2 border border-border text-sm font-semibold rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                {previewMut.isPending ? 'Rendering…' : 'Preview'}
              </button>
              {saveMut.isSuccess ? (
                <span className="text-xs text-green-600">Saved!</span>
              ) : null}
              {saveMut.isError ? (
                <span className="text-xs text-red-600">{(saveMut.error as Error).message}</span>
              ) : null}
            </div>
          </div>

          {/* ── Preview pane ── */}
          {previewHtml ? (
            <div className="bg-white border border-border rounded-xl p-5">
              <p className="text-xs font-medium text-muted mb-2">Rendered preview</p>
              <div
                data-testid="preview-pane"
                className="border border-dashed border-border rounded-lg p-4 text-xs bg-gray-50"
                // note: dangerouslySetInnerHTML used intentionally for template preview
                dangerouslySetInnerHTML={{ __html: previewHtml }}
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
